import React from "react";
import { isEqual, toString } from "lodash";
import styled from "styled-components";
import { content } from "@glossy";
import Panel from "@ui-kit/popover/panel";
import MinMax from "@radarly/components/filterbar/panels/min-max";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { tr } from "@radarly/i18n";
import { isClauseEmpty } from "@radarly/models/clause/is";
import { Clause, Range } from "@radarly/models/clause/model";
import { refineClauseWithInnerClause } from "@radarly/models/clause/refiner";
import { FullFacet } from "@radarly/models/facets";

interface IProps {
  clause: Clause;
  facet: FullFacet;
  setClause: (clause: Clause) => void;
  hasTitle?: boolean;
  onApply: () => void;
  onCancel?: () => void;
  validateLabel?: string;
  className?: string;
}

const Description = styled.div`
  margin-bottom: 24px;
  color: ${(props) => content(props, "secondary")};
`;

const getRangefromClause = (clause, facet) => {
  const rangeClauseExists = clause.and.find(
    (v) => Object.keys(v)[0] === facet.id
  );
  if (rangeClauseExists) {
    return {
      gt: toString(rangeClauseExists[facet.id].gt),
      lt: toString(rangeClauseExists[facet.id].lt),
    };
  }
  return {
    gt: toString(facet.metas.min),
    lt: toString(facet.metas.max),
  };
};

const RadarlyFilterbarPanelsMinMax = ({
  setClause,
  clause,
  facet,
  hasTitle,
  onApply,
  onCancel,
  validateLabel,
  className,
}: IProps) => {
  const locale = useCurrentUserLocale();
  const range = getRangefromClause(clause, facet);
  const isRangeEmpty = isClauseEmpty(clause);

  const formatRange = ({ gt, lt }: Range) => ({
    gt: gt === "" || isNaN(gt as any) ? null : Number(gt),
    lt: lt === "" || isNaN(lt as any) ? null : Number(lt),
  });

  const clearClause = (clause, facet) =>
    refineClauseWithInnerClause(clause, { [facet.id]: {} }, true);
  const onChangeRange = (range: Range) => {
    const formattedRange = formatRange(range);
    if (isEqual(formattedRange, { gt: null, lt: null })) {
      return setClause(clearClause(clause, facet));
    }
    return setClause(
      refineClauseWithInnerClause(clause, {
        [facet.id]: formattedRange,
      })
    );
  };

  const onClear = () => setClause(clearClause(clause, facet));

  return (
    <Panel
      locale={locale}
      onClear={onClear}
      hasTitle={hasTitle}
      title={tr(locale, "FILTERBAR::PANEL::FILTERBY", {
        filter: facet.label,
      })}
      size="s"
      onApply={onApply}
      onCancel={onCancel}
      disabled={onCancel ? isRangeEmpty : false}
      validateLabel={validateLabel}
      className={className}
    >
      {facet.description && <Description>{facet.description}</Description>}
      <MinMax range={range} onChangeRange={onChangeRange} />
    </Panel>
  );
};

export default RadarlyFilterbarPanelsMinMax;
